import { formatInTimeZone } from "date-fns-tz";
import {
  safeDecodeRoadSenseListing,
  type ReportListItem,
  type RoadSenseListingSite,
} from "@visioni/a3s-reports/client-messages";
import { formatCalendarDate } from "../../../utils/format-date";

/**
 * The RoadSense landing's rows: every (lifecycle, site) pair across the
 * tenant's reports. Kept free of React and `@src` imports so jest can test it.
 */

/** The fields of a list-route row (`ReportListItem`) this module reads. */
export interface RoadSenseListRow {
  reportId: string;
  /** `YYYY-MM-DD`. */
  periodStart: string;
  /** `YYYY-MM-DD`. */
  periodEnd: string;
  status: ReportListItem["status"];
  /** The row's opaque listing summary, decoded with `safeDecodeRoadSenseListing`. */
  listingSummary: unknown;
}

export interface RoadSenseSiteRow {
  /** Stable React key: one per (lifecycle, site), or per placeholder lifecycle. */
  key: string;
  reportId: string;
  /** Absent on a placeholder. */
  siteId?: string;
  /** Absent on a placeholder. */
  siteName?: string;
  /** "24 Apr 2026 – 15 Oct 2026" (up to the viewer's today) for an open lifecycle, its own dates for a closed one. */
  periodLabel: string;
  isOpen: boolean;
  /** Links to the site report. False only for a placeholder. */
  clickable: boolean;
  /**
   * Set on the one row standing in for a lifecycle that has never finished
   * generating (no decodable per-site list yet), which can't be opened.
   */
  placeholderStatus?: "generating" | "failed";
}

/** RoadSense's schedule zone: a lifecycle's stored `periodEnd` is a Sydney calendar date. */
const ROADSENSE_TIMEZONE = "Australia/Sydney";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * One row per (lifecycle, site), with open lifecycles first, then closed ones
 * by start date (newest first), and each lifecycle's sites by name.
 *
 * A row with sites is clickable whatever the report's status: the site report
 * is served live from the stored job, so a daily rebuild (`generating`) must
 * not disable the list. Only a lifecycle with no decodable per-site list — one
 * that has never completed a generation — becomes a single placeholder.
 */
export function roadSenseSiteRows(
  reports: readonly RoadSenseListRow[],
  now: Date,
  viewerTimezone: string,
): RoadSenseSiteRow[] {
  // Open/closed follows the daily run's Sydney schedule; the label shows the
  // viewer's own today.
  const oldestOpenEnd = oldestOpenPeriodEnd(formatInTimeZone(now, ROADSENSE_TIMEZONE, "yyyy-MM-dd"));
  const viewerToday = formatInTimeZone(now, viewerTimezone, "yyyy-MM-dd");
  const lifecycles = reports.map((report) => {
    const listing = safeDecodeRoadSenseListing(report.listingSummary);
    const sites = listing.success ? [...listing.data.sites] : [];
    // A listing that says whether the lifecycle is final is authoritative; the
    // date heuristic only covers rows written before `isFinal` existed.
    const isFinal = listing.success ? listing.data.isFinal : undefined;
    const isOpen = isFinal !== undefined ? !isFinal : report.periodEnd >= oldestOpenEnd;
    return { report, sites, isOpen };
  });
  lifecycles.sort((a, b) => {
    if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
    if (a.report.periodStart === b.report.periodStart) return 0;
    return a.report.periodStart > b.report.periodStart ? -1 : 1;
  });
  return lifecycles.flatMap(({ report, sites, isOpen }) => lifecycleRows(report, sites, isOpen, viewerToday));
}

/**
 * The oldest `periodEnd` a lifecycle without `isFinal` can have and still count
 * as open: Sydney today − 2 days. An open lifecycle's stored `periodEnd` is the
 * last completed Sydney day, but it only advances when the 06:00 tick runs, so
 * between midnight and the tick it is the day before yesterday.
 *
 * Known edges: a just-closed lifecycle reads as open (ending today) for up to
 * two days, and an open one whose tick has been missed for two or more days
 * reads as closed.
 */
function oldestOpenPeriodEnd(today: string): string {
  return new Date(Date.parse(`${today}T00:00:00Z`) - 2 * DAY_MS).toISOString().slice(0, 10);
}

function lifecycleRows(
  report: RoadSenseListRow,
  sites: RoadSenseListingSite[],
  isOpen: boolean,
  today: string,
): RoadSenseSiteRow[] {
  const periodLabel = `${formatCalendarDate(report.periodStart)} – ${formatCalendarDate(
    isOpen ? today : report.periodEnd,
  )}`;

  if (sites.length === 0) {
    return [
      {
        key: `${report.reportId}:placeholder`,
        reportId: report.reportId,
        periodLabel,
        isOpen,
        clickable: false,
        // A ready row always carries its site list, so only a drifted one lands
        // here; it reads as unavailable rather than "being generated".
        placeholderStatus: report.status === "generating" ? "generating" : "failed",
      },
    ];
  }

  sites.sort((a, b) => {
    const byName = a.siteName.localeCompare(b.siteName, "en", { sensitivity: "base" });
    if (byName !== 0) return byName;
    if (a.siteId === b.siteId) return 0;
    return a.siteId < b.siteId ? -1 : 1;
  });
  return sites.map((site) => ({
    key: `${report.reportId}:${site.siteId}`,
    reportId: report.reportId,
    siteId: site.siteId,
    siteName: site.siteName,
    periodLabel,
    isOpen,
    clickable: true,
  }));
}
